import React, { useState, useEffect } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY_FORM = {
  quote: "",
  author: "",
  designation: "",
  rating: "",
  url: "",
  projectId: "",
  clientName: "",
  isVisible: true,
  displayOrder: 0,
};

/* ── Add / Edit modal ──────────────────────────────────────────────────── */
function ReviewModal({ review, projects, onClose, onSaved }) {
  const [form, setForm] = useState(review
    ? {
        quote: review.quote || "",
        author: review.author || "",
        designation: review.designation || "",
        rating: review.rating != null ? review.rating : "",
        url: review.url || "",
        projectId: review.projectId || "",
        clientName: review.clientName || "",
        isVisible: review.isVisible !== false,
        displayOrder: review.displayOrder || 0,
      }
    : EMPTY_FORM
  );
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        quote: form.quote,
        author: form.author || null,
        designation: form.designation || null,
        rating: form.rating === "" ? null : parseFloat(form.rating),
        url: form.url || null,
        projectId: form.projectId || null,
        clientName: form.projectId ? null : (form.clientName || null),
        isVisible: form.isVisible,
        displayOrder: Number(form.displayOrder) || 0,
      };
      if (review) {
        await axios.put(`${API}/admin/reviews/${review.id}`, payload);
      } else {
        await axios.post(`${API}/admin/reviews`, payload);
      }
      onSaved();
    } catch (e) {
      alert(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    width: "100%", padding: "9px 12px", borderRadius: "6px",
    border: "1px solid #D1D5DB", fontSize: "14px", color: "#111",
    fontFamily: "inherit", outline: "none", boxSizing: "border-box",
  };
  const labelStyle = { display: "block", fontSize: "13px", fontWeight: 600,
                        color: "#374151", marginBottom: "6px" };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 100,
                  display: "flex", alignItems: "center", justifyContent: "center", padding: "24px" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: "#fff", borderRadius: "12px", width: "100%", maxWidth: "560px",
                    maxHeight: "90vh", overflowY: "auto", padding: "32px",
                    boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                      marginBottom: "24px" }}>
          <h2 style={{ margin: 0, fontFamily: "Montserrat", fontSize: "18px", fontWeight: 700,
                       color: "#111" }}>
            {review ? "Edit Review" : "Add Review"}
          </h2>
          <button onClick={onClose}
            style={{ background: "none", border: "none", fontSize: "20px", cursor: "pointer",
                     color: "#9CA3AF", lineHeight: 1 }}>×</button>
        </div>

        <div style={{ marginBottom: "18px" }}>
          <label style={labelStyle}>Quote</label>
          <textarea style={{ ...inputStyle, resize: "none" }} rows={3}
            placeholder="Quote copied from this client's Google review..."
            value={form.quote} onChange={e => set("quote", e.target.value)} />
        </div>

        <div style={{ display: "flex", gap: "12px", marginBottom: "18px" }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Reviewer Name</label>
            <input style={inputStyle} placeholder="Jane Doe" value={form.author}
              onChange={e => set("author", e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Reviewer Designation</label>
            <input style={inputStyle} placeholder="Head of Risk, NovaTech" value={form.designation}
              onChange={e => set("designation", e.target.value)} />
          </div>
        </div>

        <div style={{ marginBottom: "18px" }}>
          <label style={labelStyle}>Rating (1–5)</label>
          <input style={{ ...inputStyle, width: "120px" }} type="number" min="1" max="5" step="0.1"
            placeholder="4.9" value={form.rating} onChange={e => set("rating", e.target.value)} />
        </div>

        <div style={{ marginBottom: "18px" }}>
          <label style={labelStyle}>Google Review Link</label>
          <input style={inputStyle} type="url" placeholder="https://www.google.com/search?q=..."
            value={form.url} onChange={e => set("url", e.target.value)} />
        </div>

        <div style={{ marginBottom: "18px" }}>
          <label style={labelStyle}>Link to Project <span style={{ color: "#9CA3AF", fontWeight: 400 }}>(optional)</span></label>
          <select style={{ ...inputStyle, background: "#fff" }} value={form.projectId}
            onChange={e => set("projectId", e.target.value)}>
            <option value="">— No project (standalone) —</option>
            {projects.map(p => (
              <option key={p.id} value={p.id}>{p.title}</option>
            ))}
          </select>
        </div>

        {!form.projectId && (
          <div style={{ marginBottom: "18px" }}>
            <label style={labelStyle}>Client / Company Name <span style={{ color: "#9CA3AF", fontWeight: 400 }}>(admin-only, not shown publicly)</span></label>
            <input style={inputStyle} placeholder="e.g. Acme Inc." value={form.clientName}
              onChange={e => set("clientName", e.target.value)} />
          </div>
        )}

        <div style={{ marginBottom: "18px" }}>
          <label style={labelStyle}>Display order <span style={{ color: "#9CA3AF", fontWeight: 400 }}>(lower = first)</span></label>
          <input style={{ ...inputStyle, width: "120px" }} type="number" min="0"
            value={form.displayOrder} onChange={e => set("displayOrder", e.target.value)} />
        </div>

        <div style={{ marginBottom: "24px", display: "flex", alignItems: "center", gap: "10px" }}>
          <label style={{ position: "relative", display: "inline-block", width: "40px", height: "22px" }}>
            <input type="checkbox" checked={form.isVisible} onChange={e => set("isVisible", e.target.checked)}
              style={{ opacity: 0, width: 0, height: 0 }} />
            <span style={{
              position: "absolute", cursor: "pointer", inset: 0, borderRadius: "22px",
              background: form.isVisible ? "#2D5C5A" : "#D1D5DB",
              transition: "background 0.2s",
            }}>
              <span style={{
                position: "absolute", top: "3px", left: form.isVisible ? "21px" : "3px",
                width: "16px", height: "16px", borderRadius: "50%", background: "#fff",
                transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
              }} />
            </span>
          </label>
          <span style={{ fontSize: "13px", color: "#374151" }}>Show in homepage reviews swim lane</span>
        </div>

        <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
          <button onClick={onClose}
            style={{ padding: "10px 20px", background: "#F3F4F6", border: "1px solid #E5E7EB",
                     borderRadius: "6px", fontSize: "14px", color: "#374151", cursor: "pointer",
                     fontFamily: "inherit" }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving || !form.quote}
            style={{ padding: "10px 24px", background: "#2D5C5A", border: "none",
                     borderRadius: "6px", fontSize: "14px", color: "#fff", cursor: "pointer",
                     fontFamily: "inherit", opacity: (saving || !form.quote) ? 0.7 : 1 }}>
            {saving ? "Saving…" : review ? "Save changes" : "Add review"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Main component ────────────────────────────────────────────────────── */
export default function ReviewsAdmin() {
  const [reviews, setReviews] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState(null); // null | "add" | reviewObj
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get(`${API}/admin/reviews`);
      if (res.data?.success) setReviews(res.data.data);
    } catch {
      setError("Failed to load reviews.");
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API}/admin/portfolio`);
      if (res.data?.success) setProjects(res.data.data);
    } catch {
      // Non-fatal — the project dropdown just stays empty.
    }
  };

  useEffect(() => { fetchReviews(); fetchProjects(); }, []);

  const handleToggleVisibility = async (review) => {
    try {
      await axios.patch(`${API}/admin/reviews/${review.id}/visibility`);
      fetchReviews();
    } catch {
      alert("Failed to update visibility.");
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/admin/reviews/${deleteTarget.id}`);
      setDeleteTarget(null);
      fetchReviews();
    } catch {
      alert("Failed to delete review.");
    }
  };

  const projectTitle = (projectId) => projects.find(p => p.id === projectId)?.title;

  const th = { padding: "11px 16px", textAlign: "left", fontSize: "11px", fontWeight: 600,
               color: "#6B7280", letterSpacing: "1px", textTransform: "uppercase",
               background: "#F9FAFB", borderBottom: "1px solid #E5E7EB" };
  const td = { padding: "14px 16px", borderBottom: "1px solid #F3F4F6",
               fontSize: "13px", color: "#374151", verticalAlign: "middle" };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                    marginBottom: "28px" }}>
        <div>
          <h1 style={{ fontFamily: "Montserrat", fontSize: "22px", fontWeight: 700,
                       color: "#111", margin: 0 }}>Reviews</h1>
          <p style={{ fontSize: "13px", color: "#9CA3AF", marginTop: "4px", marginBottom: 0 }}>
            Manage Google reviews shown in the homepage swim lane, optionally linked to a project.
          </p>
        </div>
        <button onClick={() => setModal("add")}
          style={{ padding: "10px 20px", background: "#2D5C5A", color: "#fff", border: "none",
                   borderRadius: "7px", fontFamily: "Montserrat", fontWeight: 600, fontSize: "13px",
                   cursor: "pointer" }}>
          + Add review
        </button>
      </div>

      {error && (
        <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: "8px",
                      padding: "12px 16px", color: "#DC2626", fontSize: "13px", marginBottom: "20px" }}>
          {error}
        </div>
      )}

      <div style={{ background: "#fff", borderRadius: "10px", border: "1px solid #E5E7EB",
                    overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={th}>Quote</th>
              <th style={th}>Author</th>
              <th style={th}>Rating</th>
              <th style={th}>Project / Client</th>
              <th style={th}>Order</th>
              <th style={th}>Status</th>
              <th style={{ ...th, textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{ ...td, textAlign: "center", color: "#9CA3AF", padding: "40px" }}>
                Loading…
              </td></tr>
            ) : reviews.length === 0 ? (
              <tr><td colSpan={7} style={{ ...td, textAlign: "center", color: "#9CA3AF", padding: "48px" }}>
                No reviews yet. Click <strong>+ Add review</strong> to get started.
              </td></tr>
            ) : reviews.map(review => (
              <tr key={review.id} style={{ background: "#fff" }}>
                <td style={{ ...td, maxWidth: "280px" }}>
                  {review.quote.length > 60 ? `${review.quote.slice(0, 60)}…` : review.quote}
                </td>
                <td style={td}>{review.author || <span style={{ color: "#D1D5DB" }}>—</span>}</td>
                <td style={td}>{review.rating != null ? `${review.rating} ★` : <span style={{ color: "#D1D5DB" }}>—</span>}</td>
                <td style={td}>
                  {review.projectId
                    ? (projectTitle(review.projectId) || <span style={{ color: "#D1D5DB" }}>(deleted project)</span>)
                    : (review.clientName || <span style={{ color: "#D1D5DB" }}>—</span>)}
                </td>
                <td style={td}>{review.displayOrder}</td>
                <td style={td}>
                  <span style={{
                    display: "inline-block", padding: "3px 10px", borderRadius: "20px", fontSize: "11px",
                    fontWeight: 600, background: review.isVisible ? "#D1FAE5" : "#F3F4F6",
                    color: review.isVisible ? "#065F46" : "#6B7280",
                  }}>
                    {review.isVisible ? "Visible" : "Hidden"}
                  </span>
                </td>
                <td style={{ ...td, textAlign: "right" }}>
                  <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                    <button onClick={() => setModal(review)}
                      style={{ padding: "5px 12px", background: "#EFF6FF", border: "1px solid #BFDBFE",
                               borderRadius: "5px", fontSize: "12px", color: "#1D4ED8", cursor: "pointer" }}>
                      Edit
                    </button>
                    <button onClick={() => handleToggleVisibility(review)}
                      style={{ padding: "5px 12px", background: review.isVisible ? "#FEF3C7" : "#F0FDF4",
                               border: `1px solid ${review.isVisible ? "#FDE68A" : "#BBF7D0"}`,
                               borderRadius: "5px", fontSize: "12px",
                               color: review.isVisible ? "#92400E" : "#166534", cursor: "pointer" }}>
                      {review.isVisible ? "Hide" : "Show"}
                    </button>
                    <button onClick={() => setDeleteTarget(review)}
                      style={{ padding: "5px 12px", background: "#FEF2F2", border: "1px solid #FECACA",
                               borderRadius: "5px", fontSize: "12px", color: "#DC2626", cursor: "pointer" }}>
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal !== null && (
        <ReviewModal
          review={modal === "add" ? null : modal}
          projects={projects}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); fetchReviews(); }}
        />
      )}

      {deleteTarget && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 100,
                      display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={(e) => { if (e.target === e.currentTarget) setDeleteTarget(null); }}>
          <div style={{ background: "#fff", borderRadius: "10px", padding: "28px 32px",
                        maxWidth: "400px", width: "90%", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}>
            <h3 style={{ margin: "0 0 10px", fontFamily: "Montserrat", fontSize: "16px", color: "#111" }}>
              Delete review?
            </h3>
            <p style={{ margin: "0 0 24px", fontSize: "13px", color: "#6B7280" }}>
              This review will be permanently removed.
            </p>
            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <button onClick={() => setDeleteTarget(null)}
                style={{ padding: "9px 18px", background: "#F3F4F6", border: "1px solid #E5E7EB",
                         borderRadius: "6px", fontSize: "13px", color: "#374151", cursor: "pointer" }}>
                Cancel
              </button>
              <button onClick={handleDelete}
                style={{ padding: "9px 18px", background: "#EF4444", border: "none",
                         borderRadius: "6px", fontSize: "13px", color: "#fff", cursor: "pointer" }}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
